import { z } from "zod";
import i18next from "i18next";
import {
  FIELD_MAX_LENGTH,
  FIELD_MIN_TEXTS_VALID,
  FIELD_MAX_TEXTS_VALID,
  FIELD_REQUIRED,
  FIELD_TEXT_REPEATED,
  FIELD_URL_VALID_WITH_PROTOCOL,
} from "../../../../../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  ADS_CAMPAIGN_TYPE_SEARCH_OPTION,
  ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION,
  GOOGLE,
} from "../../../../../utils/constants";
import { REGEX_URL_WITH_PROTOCOL_REQUIRED } from "../../../../../utils/regex";

function getIndicesMap(arrayOfStrings) {
  const map = {};
  arrayOfStrings.forEach((txt, i) => {
    if (!map[txt]) {
      map[txt] = [];
    }
    map[txt].push(i);
  });
  return map;
}

const isGoogleSearchDsaAdGroup = ({ adGroup, campaign }) => {
  const platformData = campaign?.platformsData?.[GOOGLE];
  return (
    platformData?.objective === ADS_CAMPAIGN_TYPE_SEARCH_OPTION &&
    adGroup?.type === ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION
  );
};

const googleAdDsaSchema = ({ indexAdGroup, indexAd }) =>
  z
    .object({
      platform: z.literal(GOOGLE),
      titles: z.any().optional(),
      descriptions: z.any().optional(),
    })
    .superRefine((data, ctx) => {
      const basicPaths = ["adsGroups", indexAdGroup, "ads", indexAd];
      const descriptions = data?.descriptions || [];

      if (descriptions.length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "descriptions"],
          message: i18next.t(FIELD_MIN_TEXTS_VALID, { value: 2 }),
        });
      }
      if (descriptions.length > 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "descriptions"],
          message: i18next.t(FIELD_MAX_TEXTS_VALID, { value: 2 }),
        });
      }

      descriptions.forEach((description, i) => {
        if (!description?.value || description.value.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "descriptions", i, "value"],
            message: i18next.t(FIELD_REQUIRED),
          });
        } else if (
          description.value.length >
          ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[GOOGLE]
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "descriptions", i, "value"],
            message: i18next.t(FIELD_MAX_LENGTH, {
              value: ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[GOOGLE],
            }),
          });
        }
      });

      const descriptionsNormalized = descriptions.map((item) =>
        (item?.value || "").trim().toLowerCase()
      );
      const duplicatedDescriptions = getIndicesMap(descriptionsNormalized);
      Object.entries(duplicatedDescriptions).forEach(([key, indexes]) => {
        if (key && indexes.length > 1) {
          indexes.forEach((i) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "descriptions", i, "value"],
              message: i18next.t(FIELD_TEXT_REPEATED),
            });
          });
        }
      });
    });

const googleAdStandardSchema = ({ indexAdGroup, indexAd }) =>
  z
    .object({
      platform: z.literal(GOOGLE),
      titles: z.any().optional(),
      descriptions: z.any().optional(),
      destinationUrls: z.any().optional(),
    })
    .superRefine((data, ctx) => {
      const basicPaths = ["adsGroups", indexAdGroup, "ads", indexAd];

      if (
        !data?.destinationUrls?.length ||
        !data?.destinationUrls?.[0]?.value?.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18next.t(FIELD_REQUIRED),
          path: [...basicPaths, "destinationUrls", 0, "value"],
        });
      }
      if (
        !REGEX_URL_WITH_PROTOCOL_REQUIRED.test(
          encodeURI(data?.destinationUrls?.[0]?.value)
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18next.t(FIELD_URL_VALID_WITH_PROTOCOL),
          path: [...basicPaths, "destinationUrls", 0, "value"],
        });
      }

      data.titles.forEach((title, i) => {
        if (!title?.value || title?.value?.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "titles", i, "value"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }
        if (
          title?.value?.length > ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[GOOGLE]
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "titles", i, "value"],
            message: i18next.t(FIELD_MAX_LENGTH, {
              value: ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[GOOGLE],
            }),
          });
        }
      });

      data.descriptions.forEach((description, i) => {
        if (
          description?.value?.length >
          ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[GOOGLE]
        ) {
          if (!description?.value || description?.value?.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "descriptions", i, "value"],
              message: i18next.t(FIELD_REQUIRED),
            });
          }
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "descriptions", i, "value"],
            message: i18next.t(FIELD_MAX_LENGTH, {
              value: ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[GOOGLE],
            }),
          });
        }
      });

      const titlesNormalized = data.titles.map((item) =>
        item.value.trim().toLowerCase()
      );
      const titlesMap = getIndicesMap(titlesNormalized);
      Object.entries(titlesMap).forEach(([, indexes]) => {
        if (indexes.length > 1) {
          indexes.forEach((i) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "titles", i, "value"],
              message: i18next.t(FIELD_TEXT_REPEATED),
            });
          });
        }
      });

      const descriptionsNormalized = data.descriptions.map((item) =>
        item.value.trim().toLowerCase()
      );
      const duplicatedDescriptions = getIndicesMap(descriptionsNormalized);
      Object.entries(duplicatedDescriptions).forEach(([, indexes]) => {
        if (indexes.length > 1) {
          indexes.forEach((i) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "descriptions", i, "value"],
              message: i18next.t(FIELD_TEXT_REPEATED),
            });
          });
        }
      });
    });

export const adGoogleBaseSchema = ({
  indexAdGroup,
  indexAd,
  adGroup,
  campaign,
}) => {
  if (isGoogleSearchDsaAdGroup({ adGroup, campaign })) {
    return googleAdDsaSchema({ indexAdGroup, indexAd });
  }
  return googleAdStandardSchema({ indexAdGroup, indexAd });
};
